class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector(".quantity__input");
    this.changeEvent = new Event("change", { bubbles: true });

    // Handle click events on quantity buttons
    this.addEventListener("click", this.onButtonClick.bind(this));

    // Handle direct input changes
    this.input.addEventListener("change", this.validateInput.bind(this));
  }

  onButtonClick(event) {
    const button = event.target.closest("[data-quantity-change]");
    if (!button) return;

    event.preventDefault();

    const previousValue = this.input.value;
    const change = parseInt(button.dataset.quantityChange);
    const newValue = Math.max(1, parseInt(this.input.value) + change);

    this.input.value = newValue;
    this.input.dispatchEvent(this.changeEvent);

    // Update button states
    this.updateButtonStates();
  }

  validateInput() {
    const value = parseInt(this.input.value);

    // Ensure minimum value is 1
    if (isNaN(value) || value < 1) {
      this.input.value = 1;
    }

    // Ensure maximum value doesn't exceed max attribute
    const max = parseInt(this.input.getAttribute("max"));
    if (max && value > max) {
      this.input.value = max;
    }

    this.updateButtonStates();
  }

  updateButtonStates() {
    const value = parseInt(this.input.value);
    const min = parseInt(this.input.getAttribute("min")) || 1;
    const max = parseInt(this.input.getAttribute("max"));

    const minusButton = this.querySelector('[data-quantity-change="-1"]');
    const plusButton = this.querySelector('[data-quantity-change="1"]');

    if (minusButton) {
      minusButton.disabled = value <= min;
    }

    if (plusButton && max) {
      plusButton.disabled = value >= max;
    }
  }
}

class ProductForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector("form");
    this.submitButton = this.querySelector('[type="submit"]');
    this.variantSelects = this.querySelectorAll('select[name^="options"]');

    if (!this.form) return;

    // Handle variant changes
    this.variantSelects.forEach((select) => {
      select.addEventListener("change", this.onVariantChange.bind(this));
    });

    // Handle form submission
    this.form.addEventListener("submit", this.onSubmitHandler.bind(this));

    // Update variant on load
    this.updateVariant();
  }

  onVariantChange() {
    this.updateVariant();
  }

  updateVariant() {
    if (this.variantSelects.length === 0) return;

    const selectedOptions = Array.from(this.variantSelects).map(
      (select) => select.value
    );
    const variant = this.getVariantData().find((variant) => {
      return variant.options.every(
        (option, index) => option === selectedOptions[index]
      );
    });

    if (variant) {
      this.updateVariantInput(variant);
      this.updatePrice(variant);
      this.updateButton(variant);
    }
  }

  updateVariantInput(variant) {
    const variantInput = this.form.querySelector('input[name="id"]');
    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.disabled = !variant.available;
    }
  }

  updatePrice(variant) {
    const priceElements = document.querySelectorAll('[id^="Price-"]');

    priceElements.forEach((priceElement) => {
      const priceRegular = priceElement.querySelector(".price__regular");
      const priceSale = priceElement.querySelector(".price__sale");

      if (
        variant.compare_at_price &&
        variant.compare_at_price > variant.price
      ) {
        // Sale price
        if (priceSale) {
          priceSale.innerHTML = `
            <span class="visually-hidden">Sale price</span>
            ${this.formatMoney(variant.price)}
          `;
        }
        if (priceRegular) {
          priceRegular.innerHTML = `
            <span class="visually-hidden">Regular price</span>
            <s>${this.formatMoney(variant.compare_at_price)}</s>
          `;
        }
      } else {
        // Regular price
        if (priceRegular) {
          priceRegular.innerHTML = this.formatMoney(variant.price);
        }
        if (priceSale) {
          priceSale.innerHTML = "";
        }
      }
    });
  }

  updateButton(variant) {
    const submitButtons = document.querySelectorAll(
      '[type="submit"][name="add"]'
    );

    submitButtons.forEach((button) => {
      const buttonText = button.querySelector("span");
      if (buttonText) {
        buttonText.textContent = variant.available ? "Add to cart" : "Sold out";
      }
      button.disabled = !variant.available;
    });
  }

  getVariantData() {
    const scriptTag = document.querySelector("script[data-product-variants]");
    return scriptTag ? JSON.parse(scriptTag.textContent) : [];
  }

  formatMoney(cents) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(cents / 100);
  }

  async onSubmitHandler(event) {
    event.preventDefault();

    const submitButton = this.submitButton;
    const spinner = submitButton.querySelector(".loading-overlay__spinner");
    const buttonText = submitButton.querySelector("span");

    // Show loading state
    submitButton.disabled = true;
    if (spinner) spinner.classList.remove("hidden");

    try {
      const formData = new FormData(this.form);

      // Prepare data for cart/add.js endpoint
      const cartData = {
        items: [
          {
            id: parseInt(formData.get("id")),
            quantity: parseInt(formData.get("quantity")),
          },
        ],
      };

      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(cartData),
      });

      if (response.ok) {
        const result = await response.json();

        // Dispatch custom event for cart updates
        document.dispatchEvent(
          new CustomEvent("cart:item-added", {
            detail: { items: result.items },
          })
        );

        // Update cart count if element exists
        this.updateCartCount();

        // Reset form to normal state
        if (buttonText) buttonText.textContent = "Added to cart";
        setTimeout(() => {
          if (buttonText) buttonText.textContent = "Add to cart";
        }, 2000);
      } else {
        const error = await response.json();
        console.error("Cart add error:", error);

        if (buttonText) buttonText.textContent = "Error - Try again";
        setTimeout(() => {
          if (buttonText) buttonText.textContent = "Add to cart";
        }, 2000);
      }
    } catch (error) {
      console.error("Network error:", error);

      if (buttonText) buttonText.textContent = "Error - Try again";
      setTimeout(() => {
        if (buttonText) buttonText.textContent = "Add to cart";
      }, 2000);
    } finally {
      // Hide loading state
      submitButton.disabled = false;
      if (spinner) spinner.classList.add("hidden");
    }
  }

  async updateCartCount() {
    try {
      const response = await fetch("/cart.js");
      const cart = await response.json();

      const cartCountElements = document.querySelectorAll("[data-cart-count]");
      cartCountElements.forEach((element) => {
        const oldCount = parseInt(element.textContent) || 0;
        const newCount = cart.item_count;

        element.textContent = newCount;
        element.setAttribute("data-cart-count", newCount);

        // Add animation if count increased
        if (newCount > oldCount) {
          element.style.transform = "scale(1.3)";
          element.style.transition = "transform 0.2s ease";

          setTimeout(() => {
            element.style.transform = "scale(1)";
          }, 200);
        }
      });
    } catch (error) {
      console.error("Failed to update cart count:", error);
    }
  }
}

// Register custom elements
customElements.define("quantity-input", QuantityInput);
customElements.define("product-form", ProductForm);
